import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  AnyApiFactory,
  ApiRef,
  BackstageIdentityApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';

import {
  catalogApiRef,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import { SimpleIconsEntityPresentationApi } from '@dweber019/backstage-plugin-simple-icons';

import { tipsConfigRef } from '@dweber019/backstage-plugin-tips';
import { customTips } from './customTips';

import {
  OpenApiDefinitionWidget,
  apiDocsConfigRef,
  defaultDefinitionWidgets,
} from '@backstage/plugin-api-docs';
import { ApiEntity } from '@backstage/catalog-model';
import React from 'react';
import { Grid } from '@material-ui/core';
import { useIdentity, buildRequestInterceptor } from '@internal/shared';

import { OAuth2 } from '@backstage/core-app-api';
import YAML from 'yaml';

export const logtoOIDCAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'auth.logto-provider',
});

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  createApiFactory({
    api: entityPresentationApiRef,
    deps: { catalogApi: catalogApiRef },
    factory: ({ catalogApi }) => {
      return SimpleIconsEntityPresentationApi.create({ catalogApi });
    },
  }),
  createApiFactory({
    api: tipsConfigRef,
    deps: {},
    factory: () => {
      return {
        tips: [...customTips],
      };
    },
  }),
  createApiFactory({
    api: apiDocsConfigRef,
    deps: {
      configApi: configApiRef,
    },
    factory: ({ configApi }) => {
      // Overriding openapi definition widget to add header
      const definitionWidgets = defaultDefinitionWidgets().map(obj => {
        if (obj.type === 'openapi') {
          return {
            ...obj,
            component: (definition: any) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              const { idToken } = useIdentity();

              const definitionToFilter =
                typeof definition === 'string'
                  ? YAML.parse(definition)
                  : definition;

              const tagsToExcludeEndpoints = configApi
                .getOptionalString('openApi.excludePathsWithTags')
                ?.split(',')
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0) ?? ['x-hidden'];

              const filteredPaths = Object.fromEntries(
                Object.entries(definitionToFilter.paths ?? {}).filter(
                  ([, operations]) =>
                    !Object.values(operations as Record<string, any>).some(op =>
                      tagsToExcludeEndpoints.some(tag => op?.[tag] === true),
                    ),
                ),
              );

              const filteredDefinition = {
                ...definitionToFilter,
                paths: filteredPaths,
              };

              return (
                <Grid container spacing={2}>
                  {/* Inject style to hide the authorize button */}
                  <style>{`
                    .swagger-ui .btn.authorize {
                       margin-right: 1em !important;
                    }
                  `}</style>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <OpenApiDefinitionWidget
                          definition={filteredDefinition}
                          requestInterceptor={buildRequestInterceptor(
                            idToken,
                            configApi,
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              );
            },
          };
        }
        return obj;
      });

      return {
        getApiDefinitionWidget: (apiEntity: ApiEntity) => {
          return definitionWidgets.find(d => d.type === apiEntity.spec.type);
        },
      };
    },
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: logtoOIDCAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'logto-provider',
          title: 'Logto auth provider',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
        popupOptions: {
          // optional, used to customize login in popup size
          size: {
            fullscreen: true,
          },
        },
      }),
  }),
];
